import SectionHeading from "@/components/base/SectionHeading";
import { characters, supportingCharacters, type Character } from "@/mocks/comicData";

const accentMap: Record<
  string,
  { badge: string; tag: string; quote: string }
> = {
  primary: {
    badge: "bg-primary-100 text-primary-800",
    tag: "bg-primary-500 text-background-50",
    quote: "text-primary-700",
  },
  accent: {
    badge: "bg-accent-100 text-accent-800",
    tag: "bg-accent-500 text-background-50",
    quote: "text-accent-700",
  },
  secondary: {
    badge: "bg-secondary-100 text-secondary-800",
    tag: "bg-secondary-500 text-background-50",
    quote: "text-secondary-700",
  },
};

function CharacterCard({ character }: { character: Character }) {
  const style = accentMap[character.accent] ?? accentMap.primary;
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/80 bg-background-100">
      <div className="relative h-72 overflow-hidden">
        <img
          src={character.image}
          alt={`${character.name} · ${character.role} 角色设定`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${style.tag}`}
        >
          <i className="ri-user-fill w-3 h-3 flex items-center justify-center"></i>
          {character.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading text-xl font-bold text-foreground-950">{character.name}</h3>
            <p className="text-xs text-foreground-500">{character.age}</p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${style.badge}`}>
            {character.role.split(" · ")[0]}
          </span>
        </div>
        <p className={`font-heading text-sm font-semibold italic leading-relaxed ${style.quote}`}>
          「{character.quote}」
        </p>
        <p className="text-sm leading-relaxed text-foreground-700">{character.description}</p>
        <div className="mt-auto flex flex-wrap gap-2">
          {character.traits.map((trait) => (
            <span
              key={trait}
              className="rounded-full bg-background-50 border border-background-200 px-2.5 py-1 text-xs text-foreground-600"
            >
              {trait}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function RoleSettings() {
  return (
    <section id="roles" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <SectionHeading
          eyebrow="角色设定"
          title="继母、继姐与身边人"
          description="每一个角色都不是脸谱化的存在。这里为你拆解继母苏婉、继姐苏念的立场与性格，也补全支撑他们故事的关键配角。"
        />

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 md:mt-14">
          {characters.map((character) => (
            <CharacterCard key={character.id} character={character} />
          ))}
        </div>

        <div className="mt-10">
          <h3 className="mb-5 text-center font-heading text-lg md:text-xl font-bold text-foreground-900">
            同样重要的配角们
          </h3>
          <div className="grid gap-5 sm:grid-cols-2">
            {supportingCharacters.map((character) => (
              <div
                key={character.id}
                className="flex items-center gap-4 rounded-2xl border border-background-200/80 bg-background-50 p-4"
              >
                <img
                  src={character.image}
                  alt={`${character.name} · ${character.role}`}
                  className="h-20 w-20 shrink-0 rounded-xl object-cover object-top"
                />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground-950">{character.name}</span>
                    <span className="rounded-full bg-background-200 px-2 py-0.5 text-xs text-foreground-600">
                      {character.tag} · {character.age}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground-700">
                    {character.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}